import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, updateDoc, deleteDoc, onSnapshot, runTransaction } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Auction, AuctionLot } from '../types/auction.types';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { syncAuctionToRinger } from '../utils/webhooks';
import { GoLiveDialog, GoLiveSettings } from '../components/layout/GoLiveDialog';
import { EditSaleDialog, EditSalePayload } from '../components/layout/EditSaleDialog';
import { calculateAllLotEndTimes } from '../utils/lotTiming';
import { CountdownTimer } from '../components/auction/CountdownTimer';
import { useAuth } from '../contexts/AuthContext';
import { deriveSaleState, isBiddable, SALE_STATE_LABEL } from '../utils/saleState';
import BidFromTileModal from '../components/bidding/BidFromTileModal';
import { contentImages } from '../utils/lotImages';

const AuctionDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  // Only Admins and Auctioneers may edit sale settings (end time / description).
  const canManage = userProfile?.role === 'admin' || userProfile?.role === 'auctioneer';
  const [auction, setAuction] = useState<Auction | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedLotImages, setSelectedLotImages] = useState<string[] | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [viewMode, setViewMode] = useState<'list' | 'tile'>('list');
  const [sortBy, setSortBy] = useState<'lotNumber' | 'sku' | 'price' | 'bids'>('lotNumber');
  const [showGoLiveDialog, setShowGoLiveDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  // Bid-from-tile modal state — opens when a Bid Now button is clicked,
  // so buyers stay on the grid instead of navigating to the lot detail page.
  const [bidModalLot, setBidModalLot] = useState<AuctionLot | null>(null);

  useEffect(() => {
    if (!id) return;

    console.log('🔄 Setting up real-time auction listener for:', id);

    // Set up real-time listener for auction updates
    const unsubscribe = onSnapshot(
      doc(db, 'auctions', id),
      (auctionDoc) => {
        if (!auctionDoc.exists()) {
          console.error('❌ Auction not found');
          toast.error('Auction not found');
          navigate('/auctions');
          return;
        }

        console.log('✅ Auction data received:', auctionDoc.id);

        const data = auctionDoc.data();
        const loadedAuction: Auction = {
          id: auctionDoc.id,
          ...data,
          startDate: data.startDate?.toDate(),
          endDate: data.endDate?.toDate(),
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
          lots: data.lots.map((lot: any) => ({
            ...lot,
            createdAt: lot.createdAt?.toDate(),
            scheduledEndTime: lot.scheduledEndTime?.toDate(),
            actualEndTime: lot.actualEndTime?.toDate(),
            lastExtensionAt: lot.lastExtensionAt?.toDate(),
            lastBidAt: lot.lastBidAt?.toDate(),
          })),
        } as Auction;

        console.log('📊 Loaded lots:', loadedAuction.lots.map(l => ({
          lot: l.lotNumber,
          currentBid: l.currentBid,
          bidCount: l.bidCount,
          currentBidder: l.currentBidder
        })));

        setAuction(loadedAuction);
        setLoading(false);
      },
      (error) => {
        console.error('❌ Error loading auction:', error);
        toast.error('Failed to load auction');
        setLoading(false);
      }
    );

    // Cleanup listener on unmount
    return () => {
      console.log('🔌 Disconnecting auction listener');
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const getSortedLots = () => {
    if (!auction) return [];
    
    const lotsCopy = [...auction.lots];
    
    switch (sortBy) {
      case 'lotNumber':
        return lotsCopy.sort((a, b) => {
          const aNum = parseInt(a.lotNumber) || 0;
          const bNum = parseInt(b.lotNumber) || 0;
          return aNum - bNum;
        });
      case 'sku':
        return lotsCopy.sort((a, b) => a.sku.localeCompare(b.sku));
      case 'price':
        return lotsCopy.sort((a, b) => {
          const aPrice = a.currentBid || a.startingBid;
          const bPrice = b.currentBid || b.startingBid;
          return bPrice - aPrice;
        });
      case 'bids':
        return lotsCopy.sort((a, b) => (b.bidCount || 0) - (a.bidCount || 0));
      default:
        return lotsCopy;
    }
  };

  const handleSyncToRinger = async () => {
    if (!id || !auction) return;
    
    setSyncing(true);
    
    try {
      const result = await syncAuctionToRinger(auction);
      
      if (result.success) {
        await updateDoc(doc(db, 'auctions', id), {
          syncStatus: 'synced',
          updatedAt: new Date(),
        });
        
        toast.success('Successfully synced to AuctionRinger!');
      } else {
        toast.error(`Sync failed: ${result.error || 'Unknown error'}`);
        
        await updateDoc(doc(db, 'auctions', id), {
          syncStatus: 'failed',
          updatedAt: new Date(),
        });
      }
    } catch (error) {
      console.error('Error syncing:', error);
      toast.error('Failed to sync auction');
    } finally {
      setSyncing(false);
    }
  };

  const handleGoLive = async (settings: GoLiveSettings) => {
    if (!id || !auction) return;
    
    try {
      const auctionWithTimings: Auction = {
        ...auction,
        staggerTime: settings.staggerTime,
        softCloseWindow: settings.softCloseWindow,
        softCloseExtension: settings.softCloseExtension,
        // The backstop has to reach the document. A cap the engine reads from
        // auction.maxExtensionSeconds is no cap at all if go-live never writes it.
        maxExtensionSeconds: settings.maxExtensionSeconds,
      };
      
      const lotsWithEndTimes = calculateAllLotEndTimes(auctionWithTimings);
      
      // Clean each lot to remove undefined fields
      const cleanedLots = lotsWithEndTimes.map(lot => {
        const cleanLot: any = {
          ...lot,
          extensionCount: 0,
        };
        
        // Only add these fields if they exist
        if (lot.scheduledEndTime) {
          cleanLot.scheduledEndTime = lot.scheduledEndTime;
        }
        if (lot.actualEndTime) {
          cleanLot.actualEndTime = lot.actualEndTime;
        }
        
        // Remove any undefined values from the lot object
        return Object.fromEntries(
          Object.entries(cleanLot).filter(([_, v]) => v !== undefined)
        );
      });
      
      // Build update object
      const updateData: any = {
        lots: cleanedLots,
        status: 'live',
        liveAt: new Date().toISOString(),
        updatedAt: new Date(),
      };
      
      // Only add optional fields if they're defined
      if (settings.staggerTime !== undefined) {
        updateData.staggerTime = settings.staggerTime;
      }
      if (settings.softCloseWindow !== undefined) {
        updateData.softCloseWindow = settings.softCloseWindow;
      }
      if (settings.maxExtensionSeconds !== undefined) {
        updateData.maxExtensionSeconds = settings.maxExtensionSeconds;
      }
      if (settings.softCloseExtension !== undefined) {
        updateData.softCloseExtension = settings.softCloseExtension;
      }
      if (settings.coolOffWindow !== undefined) {
        updateData.coolOffWindow = settings.coolOffWindow;
      }
      
      const auctionRef = doc(db, 'auctions', id);
      await updateDoc(auctionRef, updateData);
      
      await handleSyncToRinger();
      
      if (settings.uploadToAuctionZip) {
        console.log('Uploading to AuctionZip with settings:', settings);
      }
      
      toast.success('Auction is now LIVE with staggered lot closings!');
    } catch (error) {
      console.error('Error going live:', error);
      toast.error('Failed to go live');
    }
  };

  /**
   * EDIT SALE — change end time + description text. Runs in a transaction so a bid landing
   * mid-write can't be clobbered (placeBid is a non-atomic lots[] rewrite). If the end time
   * changed, EVERY lot's clock is shifted by the same delta (scheduledEndTime, actualEndTime,
   * and lastExtensionAt when present), preserving current spacing — the same behavior as the
   * move-close terminal script. extensionCount is preserved (not reset). Text-only edits skip
   * the lots rewrite entirely.
   */
  const handleEditSale = async (payload: EditSalePayload) => {
    if (!id) return;
    const auctionRef = doc(db, 'auctions', id);

    // Coerce a Firestore Timestamp | Date | serialized shape to epoch ms (or null).
    const toMs = (v: any): number | null => {
      if (v == null) return null;
      if (typeof v.toMillis === 'function') return v.toMillis();
      if (v instanceof Date) return isNaN(v.getTime()) ? null : v.getTime();
      if (typeof v.seconds === 'number') return v.seconds * 1000 + (v.nanoseconds || 0) / 1e6;
      const d = new Date(v);
      return isNaN(d.getTime()) ? null : d.getTime();
    };

    try {
      await runTransaction(db, async (tx) => {
        const fresh = await tx.get(auctionRef);
        if (!fresh.exists()) throw new Error('Auction not found');
        const data = fresh.data() as any;

        const update: any = {
          description: payload.description,
          'saleInfo.dateTimeInfo': payload.dateTimeInfo,
          updatedAt: new Date(),
        };

        // TITLE. Only written when it is non-empty AND actually different, so an
        // untouched dialog never rewrites the sale name, and an accidentally
        // cleared box cannot blank it. The title is public on the sale page, every
        // lot page, Past Auctions and outgoing email — it is not a field to lose
        // to a stray keystroke.
        const nextTitle = (payload.title || '').trim();
        if (nextTitle && nextTitle !== (data.title || '')) {
          update.title = nextTitle;
        }

        const freshEndMs = toMs(data.endDate);
        const delta = freshEndMs != null ? payload.endDate.getTime() - freshEndMs : 0;

        if (delta !== 0) {
          update.endDate = payload.endDate;
          const lots = Array.isArray(data.lots) ? data.lots : [];
          update.lots = lots.map((lot: any) => {
            const out: any = { ...lot };
            const s = toMs(lot.scheduledEndTime);
            const a = toMs(lot.actualEndTime);
            const le = toMs(lot.lastExtensionAt);
            if (s != null) out.scheduledEndTime = new Date(s + delta);
            if (a != null) out.actualEndTime = new Date(a + delta);
            if (le != null) out.lastExtensionAt = new Date(le + delta);
            return out;
          });
        }

        tx.update(auctionRef, update);
      });
      toast.success('Sale updated');
    } catch (error) {
      console.error('Error updating sale:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update sale');
      throw error; // keep the dialog open and surface the error
    }
  };

  const handleDelete = async () => {
    if (!id) return;

    if (!window.confirm('Are you sure you want to delete this auction? This cannot be undone.')) {
      return;
    }
    
    try {
      await deleteDoc(doc(db, 'auctions', id));
      toast.success('Auction deleted successfully');
      navigate('/auctions');
    } catch (error) {
      console.error('Error deleting auction:', error);
      toast.error('Failed to delete auction');
    }
  };

  // 'completed' dropped — legacy closedown data still contains it, but nothing writes it now
  // (normalizeStatus maps completed -> ended on read). 'confirmed' is the auctioneer's
  // authoritative lock: it is the SEAM the invoice pipeline will hang off later.
  const handleStatusChange = async (newStatus: 'draft' | 'scheduled' | 'live' | 'ended' | 'confirmed') => {
    if (!id) return;
    
    try {
      await updateDoc(doc(db, 'auctions', id), {
        status: newStatus,
        updatedAt: new Date(),
      });
      toast.success(`Auction status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  /**
   * The photos to show, internal frames removed.
   *
   * This used to be `images.length > 1 ? images.slice(1) : []` — drop whatever
   * is in slot 0 and hope it was the _00 barcode. Measured against the live
   * data on 2026-10-01, no sale has a _00 frame in its lots[] at all: the
   * September sale's 190 lots with images all start at _01, and so do Ed's 176.
   * So the slice was throwing away a real photo on every lot in both sales.
   *
   * It stayed invisible for a year because September's lots carry 3-32 images
   * (median 5) — losing one of six and starting at the second looks like a
   * normal gallery. Ed's lots carry exactly 2 frames, where the same bug reads
   * as "1 image" with the second one shown first.
   *
   * contentImages() decides by FILENAME (lotImages.ts), so it does not depend
   * on array order and does not need a _00 frame to exist. Same change
   * LotDetail.tsx already carries.
   */
  const getDisplayImages = (images: string[]) => contentImages(images);

  const openImageGallery = (images: string[], startIndex: number = 0) => {
    const displayImages = getDisplayImages(images);
    setSelectedLotImages(displayImages);
    setCurrentImageIndex(startIndex);
  };

  const closeImageGallery = () => {
    setSelectedLotImages(null);
    setCurrentImageIndex(0);
  };

  const nextImage = () => {
    if (selectedLotImages) {
      setCurrentImageIndex((prev) => 
        prev < selectedLotImages.length - 1 ? prev + 1 : 0
      );
    }
  };

  const prevImage = () => {
    if (selectedLotImages) {
      setCurrentImageIndex((prev) => 
        prev > 0 ? prev - 1 : selectedLotImages.length - 1
      );
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Loading auction...</p>
      </div>
    );
  }

  if (!auction) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Auction not found</p>
      </div>
    );
  }

  // Display state is DERIVED (matches the public page), not raw stored status. The engine
  // never writes 'closing'/'ended'; only 'draft'/'scheduled'/'live'/'confirmed' are stored.
  const saleState = deriveSaleState(auction);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-4 mb-2">
            <button
              onClick={() => navigate('/auctions')}
              className="text-primary-600 hover:text-primary-700 flex items-center gap-2"
            >
              ← Back to Auctions
            </button>
            <button
              onClick={handleDelete}
              className="text-red-600 hover:text-red-700 flex items-center gap-2"
            >
              🗑️ Delete Auction
            </button>
          </div>
          <h1 className="text-3xl font-bold">{auction.title}</h1>
          <p className="text-gray-600 mt-1">
            {format(auction.startDate, 'MMMM dd, yyyy h:mm a')} -{' '}
            {format(auction.endDate, 'h:mm a')}
          </p>
        </div>
        <div className="flex flex-col items-end gap-3">
          <span
            className={`px-4 py-2 rounded-full font-semibold ${
              saleState === 'live' || saleState === 'closing'
                ? 'bg-green-100 text-green-800'
                : saleState === 'scheduled'
                ? 'bg-blue-100 text-blue-800'
                : saleState === 'draft'
                ? 'bg-gray-100 text-gray-800'
                : 'bg-purple-100 text-purple-800'
            }`}
          >
            {SALE_STATE_LABEL[saleState]}
          </span>
          
          {(auction.status === 'draft' || auction.status === 'scheduled') && (
            <button
              onClick={() => setShowGoLiveDialog(true)}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
            >
              ⚙️ Configure & Go Live
            </button>
          )}
          
          {saleState === 'live' && (
            <div className="px-6 py-2 bg-green-100 text-green-800 rounded-lg font-semibold border-2 border-green-600">
              🟢 LIVE - Accepting Bids
            </div>
          )}
          
          <div className="flex gap-2">
            <button
              onClick={() => handleStatusChange('draft')}
              disabled={auction.status === 'draft'}
              className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Draft
            </button>
            <button
              onClick={() => handleStatusChange('scheduled')}
              disabled={auction.status === 'scheduled'}
              className="px-3 py-1 bg-blue-100 hover:bg-blue-200 rounded text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Scheduled
            </button>
            {/* ── CONFIRM BIDS — the auctioneer's authoritative ended -> confirmed lock. ──
                This is the SEAM: the invoice pipeline (lock → generate invoices → publish
                final prices) hangs off this transition in a SEPARATE build. Today it only
                flips the status, which makes the homepage publish official final prices. */}
            {saleState === 'ended' && (
              <button
                onClick={() => {
                  if (window.confirm(
                    'Confirm bids and publish OFFICIAL final prices?\n\n' +
                    'This locks the results and publishes final prices publicly. ' +
                    'Do this only after the cool-off / tally window has elapsed.'
                  )) handleStatusChange('confirmed');
                }}
                className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-semibold"
              >
                ✓ Confirm Bids (publish final)
              </button>
            )}
            {saleState === 'confirmed' && (
              <span className="px-3 py-1 bg-gray-800 text-white rounded text-sm font-semibold">
                ✓ Results Confirmed
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Admin Action Buttons */}
      {currentUser && (
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="font-bold text-gray-900 mb-3">Auction Management</h3>
          <div className="flex flex-wrap gap-3">
            {canManage && (
              <button
                onClick={() => setShowEditDialog(true)}
                className="px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-semibold flex items-center gap-2"
              >
                ✏️ Edit Sale (time &amp; description)
              </button>
            )}
            <Link
              to={`/auctions/${auction.id}/invoices`}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold flex items-center gap-2"
            >
              📄 View Invoices
            </Link>
            <Link
              to={`/admin/sale/${auction.id}/settlements`}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold flex items-center gap-2"
            >
              🧾 Consignor Settlements
            </Link>
            <Link
              to="/picker"
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold flex items-center gap-2"
            >
              📦 Warehouse Picker
            </Link>
            {saleState === 'ended' && (
              <button
                onClick={() => navigate('/auction-closedown')}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold flex items-center gap-2"
              >
                💰 Process Closedown
              </button>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-4 items-center flex-wrap">
        <Link 
          to={`/auctions/${id}/update-images`}
          className="btn-secondary"
        >
          🖼️ Update Images
        </Link>
        
        <Link 
          to={`/auctions/${id}/update-csv`}
          className="btn-secondary"
        >
          📝 Re-import CSV
        </Link>

        <button
          onClick={handleSyncToRinger}
          disabled={syncing}
          className="btn-secondary"
        >
          {syncing 
            ? '⏳ Syncing...' 
            : auction.syncStatus === 'synced' 
              ? '🔄 Re-sync to AuctionRinger' 
              : '🔄 Push to AuctionRinger'
          }
        </button>

        <div className="flex items-center gap-2">
          <label className="text-sm font-semibold text-gray-700">Sort by:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="lotNumber">Lot Number</option>
            <option value="sku">SKU</option>
            <option value="price">Current Bid (High to Low)</option>
            <option value="bids">Most Bids</option>
          </select>
        </div>

        <div className="flex gap-2 border rounded-lg p-1 bg-gray-50 ml-auto">
          <button
            onClick={() => setViewMode('list')}
            className={`px-4 py-2 rounded transition ${
              viewMode === 'list'
                ? 'bg-white shadow-sm font-semibold'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            📋 List View
          </button>
          <button
            onClick={() => setViewMode('tile')}
            className={`px-4 py-2 rounded transition ${
              viewMode === 'tile'
                ? 'bg-white shadow-sm font-semibold'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            🎴 Tile View
          </button>
        </div>
      </div>
      
      {/* Details */}
      <div className="card">
        <h2 className="text-xl font-bold mb-4">Auction Details</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <p className="text-sm text-gray-600">Total Lots</p>
            <p className="text-2xl font-bold text-primary-600">
              {auction.totalLots}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Location</p>
            <p className="text-lg font-semibold">
              {auction.location || 'Not specified'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">AuctionRinger Sync</p>
            <p className="text-lg font-semibold">
              {auction.syncStatus === 'synced' ? (
                <span className="text-green-600">✓ Synced</span>
              ) : auction.syncStatus === 'pending' ? (
                <span className="text-yellow-600">○ Pending</span>
              ) : (
                <span className="text-red-600">✗ Failed</span>
              )}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Imported From</p>
            <p className="text-lg font-semibold">
              {auction.importedFrom === 'hibid' ? 'HiBid CSV' : 'Manual'}
            </p>
          </div>
        </div>
        {auction.description && (
          <div className="mt-6">
            <p className="text-sm text-gray-600 mb-2">Description</p>
            <p className="text-gray-800">{auction.description}</p>
          </div>
        )}
      </div>

      {/* Lots - List View */}
      {viewMode === 'list' && (
        <div className="card">
          <h2 className="text-xl font-bold mb-4">Lots ({auction.lots.length})</h2>
          <div className="space-y-4 max-h-[600px] overflow-y-auto">
            {getSortedLots().map((lot, i) => {
              const displayImages = getDisplayImages(lot.images || []);
              const currentBid = lot.currentBid || lot.startingBid;
              const isWinning = currentUser && lot.currentBidder === currentUser.uid;
              const hasBids = (lot.bidCount || 0) > 0;
              
              return (
                <Link
                  to={`/auctions/${auction.id}/lots/${lot.id}`}
                  key={i}
                  className={`block border-2 rounded-lg p-4 hover:shadow-md transition ${
                    isWinning ? 'border-green-500 bg-green-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Thumbnail */}
                    {displayImages.length > 0 ? (
                      <div className="flex-shrink-0">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            openImageGallery(lot.images, 0);
                          }}
                          className="relative group"
                        >
                          <img
                            src={displayImages[0]}
                            alt={lot.title}
                            className="w-32 h-32 object-cover rounded border-2 border-gray-200 group-hover:border-primary-500 transition"
                          />
                          {displayImages.length > 1 && (
                            <span className="absolute bottom-1 right-1 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                              +{displayImages.length - 1}
                            </span>
                          )}
                        </button>
                      </div>
                    ) : (
                      <div className="flex-shrink-0 w-32 h-32 bg-gray-100 rounded border-2 border-gray-200 flex items-center justify-center">
                        <span className="text-gray-400 text-sm">No Image</span>
                      </div>
                    )}

                    {/* Lot Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm text-gray-600">
                          LOT {lot.lotNumber} <span className="text-gray-400">(SKU: {lot.sku})</span>
                        </p>
                        {isWinning && (
                          <span className="px-2 py-1 bg-green-500 text-white text-xs font-bold rounded">
                            🏆 WINNING
                          </span>
                        )}
                      </div>
                      <p className="font-bold text-lg mt-1">{lot.title}</p>
                      <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                        {lot.description}
                      </p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {(lot.hibidCategory || lot.category) && (
                          <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                            📁 {lot.hibidCategory || lot.category}
                          </span>
                        )}
                        {lot.measures && (
                          <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                            📏 {lot.measures}
                          </span>
                        )}
                        {displayImages.length > 0 && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              openImageGallery(lot.images, 0);
                            }}
                            className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded hover:bg-green-200 transition"
                          >
                            📸 {displayImages.length} {displayImages.length === 1 ? 'image' : 'images'}
                          </button>
                        )}
                        {hasBids && (
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                            🔨 {lot.bidCount} {lot.bidCount === 1 ? 'bid' : 'bids'}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Price & Status */}
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm text-gray-600">
                        {saleState === 'ended' 
                          ? 'Final Bid' 
                          : hasBids 
                            ? 'Current Bid' 
                            : 'Starting Bid'}
                      </p>
                      <p className={`font-bold text-xl ${
                        saleState === 'ended'
                          ? 'text-gray-700'
                          : isWinning 
                            ? 'text-green-600' 
                            : 'text-primary-600'
                      }`}>
                        ${(currentBid / 100).toFixed(2)}
                      </p>
                      
                      {saleState === 'ended' && hasBids && (
                        <p className="text-xs text-gray-500 mt-1">
                          {lot.bidCount} {lot.bidCount === 1 ? 'bid' : 'bids'}
                        </p>
                      )}
                      
                      {isBiddable(saleState) && lot.actualEndTime && (
                        <div className="mt-2">
                          <CountdownTimer
                            endTime={lot.actualEndTime}
                            scheduledEndTime={lot.scheduledEndTime}
                            hardStop={auction.biddingHardStop}
                            softCloseWindowSeconds={auction.softCloseWindow}
                            size="small"
                          />
                        </div>
                      )}
                      
                      {isBiddable(saleState) && (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            setBidModalLot(lot);
                          }}
                          className="mt-2 px-3 py-1 bg-primary-600 text-white rounded text-xs font-semibold hover:bg-primary-700"
                        >
                          {isWinning ? 'Manage Bid' : 'Bid Now'}
                        </button>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Lots - Tile View */}
      {viewMode === 'tile' && (
        <div className="card">
          <h2 className="text-xl font-bold mb-4">Lots ({auction.lots.length})</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-h-[600px] overflow-y-auto">
            {getSortedLots().map((lot, i) => {
              const displayImages = getDisplayImages(lot.images || []);
              const currentBid = lot.currentBid || lot.startingBid;
              const isWinning = currentUser && lot.currentBidder === currentUser.uid;
              const hasBids = (lot.bidCount || 0) > 0;
              
              return (
                <Link
                  to={`/auctions/${auction.id}/lots/${lot.id}`}
                  key={i}
                  className={`block border-2 rounded-lg overflow-hidden hover:shadow-lg transition relative ${
                    isWinning ? 'border-green-500' : 'border-gray-200'
                  }`}
                >
                  {/* Winning Badge */}
                  {isWinning && (
                    <div className="absolute top-2 right-2 z-10">
                      <span className="px-2 py-1 bg-green-500 text-white text-xs font-bold rounded shadow-lg">
                        🏆 WINNING
                      </span>
                    </div>
                  )}
                  
                  {/* Image */}
                  {displayImages.length > 0 ? (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        openImageGallery(lot.images, 0);
                      }}
                      className="relative w-full group"
                    >
                      <img
                        src={displayImages[0]}
                        alt={lot.title}
                        className="w-full h-48 object-cover group-hover:opacity-90 transition"
                      />
                      {displayImages.length > 1 && (
                        <span className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                          +{displayImages.length - 1}
                        </span>
                      )}
                    </button>
                  ) : (
                    <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
                      <span className="text-gray-400 text-sm">No Image</span>
                    </div>
                  )}

                  {/* Content */}
                  <div className={`p-3 ${isWinning ? 'bg-green-50' : ''}`}>
                    <p className="text-xs text-gray-500 font-semibold">
                      LOT {lot.lotNumber}
                    </p>
                    <p className="font-bold text-sm mt-1 line-clamp-2">{lot.title}</p>
                    
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-xs text-gray-500">{hasBids ? 'Current Bid' : 'Starting Bid'}</p>
                      <p className={`font-bold text-lg ${isWinning ? 'text-green-600' : 'text-primary-600'}`}>
                        ${(currentBid / 100).toFixed(2)}
                      </p>
                      
                      {isBiddable(saleState) && lot.actualEndTime && (
                        <div className="mt-1">
                          <CountdownTimer
                            endTime={lot.actualEndTime}
                            scheduledEndTime={lot.scheduledEndTime}
                            hardStop={auction.biddingHardStop}
                            softCloseWindowSeconds={auction.softCloseWindow}
                            size="small"
                          />
                        </div>
                      )}
                      
                      {hasBids && (
                        <p className="text-xs text-purple-600 mt-1">
                          🔨 {lot.bidCount} {lot.bidCount === 1 ? 'bid' : 'bids'}
                        </p>
                      )}
                      
                      {isBiddable(saleState) && (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            setBidModalLot(lot);
                          }}
                          className="w-full mt-2 px-3 py-1 bg-primary-600 text-white rounded text-xs font-semibold hover:bg-primary-700"
                        >
                          {isWinning ? 'Manage Bid' : 'Bid Now'}
                        </button>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Image Gallery Modal */}
      {selectedLotImages && selectedLotImages.length > 0 && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={closeImageGallery}
        >
          <button
            onClick={closeImageGallery}
            className="absolute top-4 right-4 text-white text-4xl hover:text-gray-300 z-50"
          >
            ×
          </button>

          {selectedLotImages.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                className="absolute left-4 text-white text-6xl hover:text-gray-300 z-50"
              >
                ‹
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                className="absolute right-4 text-white text-6xl hover:text-gray-300 z-50"
              >
                ›
              </button>
            </>
          )}

          <div
            className="max-w-5xl max-h-[90vh] relative"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedLotImages[currentImageIndex]}
              alt={`Lot photo ${currentImageIndex + 1}`}
              className="max-w-full max-h-[90vh] object-contain rounded"
            />
            {selectedLotImages.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-75 text-white px-4 py-2 rounded">
                {currentImageIndex + 1} / {selectedLotImages.length}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Go Live Dialog */}
      {showGoLiveDialog && auction && (
        <GoLiveDialog
          auction={auction}
          onClose={() => setShowGoLiveDialog(false)}
          onGoLive={handleGoLive}
        />
      )}

      {/* Edit Sale Dialog — end time + description, admin/auctioneer only */}
      {showEditDialog && auction && canManage && (
        <EditSaleDialog
          auction={auction}
          onClose={() => setShowEditDialog(false)}
          onSave={handleEditSale}
        />
      )}

      {/* Bid-from-tile modal — added for the Sunday Shop Cleanout test.
          Lets buyers bid without leaving the grid. Handles guest registration
          inline (email + name) for first-time bidders. */}
      {bidModalLot && auction && (
        <BidFromTileModal
          auction={auction}
          /* LIVE lookup from the real-time auction listener — never the copy captured on click. */
          lot={auction.lots.find((l) => l.id === bidModalLot.id) ?? bidModalLot}
          onClose={() => setBidModalLot(null)}
        />
      )}
    </div>
  );
};

export default AuctionDetails;
